import React, { useState } from "react";
import { Modal, Form, Button, SelectPicker, Input, Checkbox } from "rsuite";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import CheckIcon from "@rsuite/icons/Check";

export const mentionedUserId = "U094R7757DK"; //slack id of Anindita

const ProductEnquiry = ({ enquiredProduct, modalOpen, setModalOpen }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [enquiryPurpose, setEnquiryPurpose] = useState(enquiredProduct?.title);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setModalOpen(false);
  };

  /**
   * Validates the form fields for the enquiry form and returns validation status.
   *
   * The function checks the following criteria:
   * - Name must only contain alphabets and spaces.
   * - Phone must only contain numeric characters.
   * - Remarks cannot be empty.
   *
   * If any field is invalid, an alert is displayed with the first validation error encountered.
   *
   * @returns {boolean} - Returns `true` if all fields are valid, otherwise `undefined`.
   *
   */
  const validateFields = () => {
    const newErrors = {};
    // Validate form fields
    const namePattern = /^[a-zA-Z\s]*$/;
    const phonePattern = /^\d+$/;

    if (!name || !namePattern.test(name)) {
      newErrors.name = "Please enter a valid name (alphabets and spaces only).";
    }

    if (!phone || !phonePattern.test(phone)) {
      newErrors.phone = "Please enter a valid phone number (numbers only).";
    }

    if (!remarks) {
      newErrors.remarks = "Remarks cannot be empty.";
    }

    if (Object.keys(newErrors).length !== 0) {
      alert(Object.values(newErrors)[0]);
    } else return true; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    if (isSubmitting) return;

    if (!validateFields) return;

    setIsSubmitting(true);
    try {
      await fetch(`${process.env.REACT_APP_GSCRIPT_URL}?function=updateData`, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          contactNumber: phone,
          enquiryPurpose: enquiredProduct?.title,
          remarks,
        }),
      });

      setModalOpen(false);
      await sendSlackNotification({
        name,
        phone,
        enquiredProduct: enquiredProduct?.title,
        remarks,
      });
    } catch (error) {
      alert("Error submitting enquiry");
      setIsSubmitting(false);
    } finally {
      // Reset form fields
      setName("");
      setPhone("");
      setEnquiryPurpose("");
      setRemarks("");
      // Re-enable the button after one minute
      setTimeout(() => {
        setIsSubmitting(false);
      }, 60000);
    }
  };

  const sendSlackNotification = async ({
    name,
    phone,
    enquiredProduct,
    remarks,
  }) => {
    const slackWebhookUrl =
      "https://hooks.slack.com/services/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";

    // Constructing the Slack message with blocks for enhanced formatting
    const payload = {
      blocks: [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*New Enquiry Received*`,
          },
        },
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `<@${mentionedUserId}> You have a new enquiry:`,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Name:*\n${name}`,
            },
            {
              type: "mrkdwn",
              text: `*Contact Number:*\n${phone}`,
            },
          ],
        },
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*Enquiry Purpose:*\n${
              enquiredProduct || "No product specified"
            }`,
          },
        },
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `*Remarks:*\n${remarks || "No remarks provided"}`,
          },
        },
      ],
    };

    try {
      // Sending the formatted message to Slack
      await fetch(slackWebhookUrl, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      console.log("Slack notification sent successfully");
    } catch (error) {
      console.error("Error sending Slack notification:", error);
    }
  };

  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Modal.Header>
        <Modal.Title> Enquiry Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid onSubmit={handleSubmit}>
          <Form.Group>
            <Form.ControlLabel for="name">
              <span>Name</span>
            </Form.ControlLabel>
            <Form.Control
              name="name"
              type="text"
              placeholder="Enter your name here"
              Required
              value={name}
              onChange={(value) => setName(value)}
            />
            {/* <Form.HelpText>Required</Form.HelpText> */}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="phone">
              <span>Contact Number</span>
            </Form.ControlLabel>
            <Form.Control
              name="phone"
              type="tel"
              placeholder="Enter your contact number"
              value={phone}
              onChange={(value) => setPhone(value)}
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="entry_purpose">
              <span>Enquiry Purpose</span>
            </Form.ControlLabel>
            <Form.Control
              name="enquiry-purpose"
              placeholder="Enter your Enquiry Purpose"
              value={enquiredProduct?.title}
              readOnly
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="remark">
              <span>Remark</span>
            </Form.ControlLabel>
            <Input
              value={remarks}
              onChange={(value) => setRemarks(value)}
              as="textarea"
              name="remark"
              rows={7}
              placeholder="Enter your remarks here"
            />
          </Form.Group>

          <Form.Group style={{ float: "right" }}>
            <Button disabled={isSubmitting} type="submit">
              <CheckIcon /> Submit Enquiry
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ProductEnquiry;
